import { OrganizationTypeCodes } from 'fhir/Organization/constants'
import { getValidatedFragment } from 'fhir/utils'
import {
  BaseOrganizationFragment,
  BasePractitionerFragment,
  BasePractitionerRoleFragment,
  BasePractitionerRoleWithOrganizationFragment,
  BasePractitionerWithRolesOrgFragment,
  PractitionerData_GetPractitionerQuery,
  PractitionerRole
} from 'generated/graphql'
import { findValueBySystem } from 'utils/helpers'
import { PractitionerListSchema, PractitionerWithRoleInfoSchema } from 'store/practitioner'
import { isPractitionerFragment } from 'components/Schedule/utils/typeguards'
import {
  NPPES_PROVIDER_TAXONOMY_SYSTEM,
  PRACTITIONER_GROUP_IDENTIFIER_SYSTEM,
  PRACTITIONER_TITLE_SYSTEM,
  ProviderIdentifierType,
  UserGroup
} from './constants'

export const selectPrimaryPractitionerOrg = (
  practitioner: BasePractitionerWithRolesOrgFragment
): BaseOrganizationFragment | null => {
  const roles =
    practitioner?.PractitionerRoleList?.filter(
      (role): role is BasePractitionerRoleWithOrganizationFragment => !!role
    ) ?? []

  if (roles.length === 0) {
    return null
  }

  const organizations = roles
    .map((role) => getValidatedFragment<BaseOrganizationFragment>(role.organization?.resource))
    .filter((o): o is BaseOrganizationFragment => !!o)

  const organization = organizations[0]
  if (!organization) {
    return null
  }

  return organization
}

export const getPractitionerSex = (practitionerData: BasePractitionerFragment): string | null => {
  const practitionerSexCode = practitionerData.birthSexExtension
    ?.flatMap((e) => e?.valueCodeableConcept?.coding?.map((c) => c?.code))
    ?.find((code) => !!code)

  return practitionerSexCode ?? null
}

export const getPractitionerPhone = (
  practitionerData: BasePractitionerFragment
): string | undefined => {
  const practitionerPhone = practitionerData.telecom
    ? findValueBySystem(practitionerData.telecom, 'phone')
    : undefined

  return practitionerPhone
}

export interface PractitionerWithRoleList extends BasePractitionerFragment {
  PractitionerRoleList: BasePractitionerRoleFragment[]
}

export const isAbbyCarePractitioner = (
  roles: PractitionerRole[] | Array<BasePractitionerRoleFragment | null>
): boolean => {
  return roles.some((role) =>
    role?.organization?.resource?.type?.some((type) =>
      type?.coding?.some((coding) => coding?.code === OrganizationTypeCodes.PROVIDER)
    )
  )
}

export const findAbbyCareRegisteredNurse = (
  registeredNurses: PractitionerWithRoleList[]
): PractitionerWithRoleList[] => {
  return (
    registeredNurses?.filter((nurse) => isAbbyCarePractitioner(nurse.PractitionerRoleList)) ?? []
  )
}

export const getPractitionerTimezone = (
  practitioner: PractitionerData_GetPractitionerQuery['Practitioner']
): string | undefined => {
  if (!practitioner) return undefined
  if ('timezoneExtension' in practitioner) {
    return (
      practitioner.timezoneExtension?.find((extension) => extension.valueCoding?.code)?.valueCoding
        ?.code ?? undefined
    )
  }
}

export const getUniquePractitioners = (
  practitioners: PractitionerListSchema
): PractitionerListSchema => {
  const seen = new Set<string>()
  return practitioners?.filter((practitioner) => {
    if (isPractitionerFragment(practitioner) && practitioner.id) {
      const uniqueKey = practitioner.id
      if (seen.has(uniqueKey)) {
        return false
      }
      seen.add(uniqueKey)
      return true
    }
    return false
  })
}

/**
 * Filters practitioners by their user group.
 */
export const filterPractitionerByUserGroup = (
  practitioner: PractitionerWithRoleInfoSchema,
  userGroup: UserGroup
): boolean => {
  return (
    practitioner?.identifier?.some(
      (identifier) =>
        identifier.system === PRACTITIONER_GROUP_IDENTIFIER_SYSTEM && identifier.value === userGroup
    ) ?? false
  )
}

export type PractitionerLicenseInfo = {
  licenseNumber: string | null
  licenseType: string | null
  providerTitle: { code?: string | null; display?: string | null }
}

export const getPractitionerLicenseInfo = (
  practitioner: BasePractitionerFragment | null | undefined
): PractitionerLicenseInfo => {
  const providerLicenseQualification =
    practitioner?.qualification?.find((q) =>
      q.identifier?.find((id) => id.system === ProviderIdentifierType.LICENSE)
    ) ?? null
  const providerTitleQualification =
    practitioner?.qualification?.find((q) =>
      q.code?.coding?.find((c) => c.system === PRACTITIONER_TITLE_SYSTEM)
    ) ?? null
  const licenseNumber =
    providerLicenseQualification?.identifier?.find(
      (id) => id.system === ProviderIdentifierType.LICENSE
    )?.value ?? null
  const licenseType =
    providerLicenseQualification?.code?.coding?.find(
      (c) => c.system === NPPES_PROVIDER_TAXONOMY_SYSTEM
    )?.display ?? null
  const providerTitleCode =
    providerTitleQualification?.code?.coding?.find((c) => c.system === PRACTITIONER_TITLE_SYSTEM) ??
    null
  const providerTitle = { code: providerTitleCode?.code, display: providerTitleCode?.display }
  return { licenseNumber, licenseType, providerTitle }
}
